/**
 * PDP gallery arrows, placed on the PAINTED image (2026-09-27, round 2).
 *
 * Scope: sections/nysta-product.liquid's `.nysta-product-media` only. Card
 * carousels, the home carousels and the zoom dialog are not touched.
 *
 * Why: Horizon's <slideshow-arrows> is `inset: 0` on the slide box. On the Nysta
 * PDP the image sits inside a field (--nysta-media-well-pad-inline, 32px) and is
 * contain-fitted, so the icons landed ~11px OUTSIDE a full-width image and further
 * out on tall images -- on the field, near the divider to the next column.
 * Measured on Isager Hør Organic at 1440: slide 147-846, painted image 179-814,
 * icons at 168 and 825.
 *
 * What: for the slide in view, compute the painted rect (content box + contain +
 * object-position) and set padding-inline on <slideshow-arrows> so each icon's
 * outer edge sits GAP px inside the image, symmetrically. The arrows keep their
 * look, size, hit area (the 44px button), vertical centring and keyboard focus.
 * Without JS the CSS fallback in nysta-product.liquid covers full-width images.
 */
(() => {
  if (window.__nystaGalleryArrows) return;
  window.__nystaGalleryArrows = true;

  const GAP = 16; // icon edge -> image edge, px

  /** @param {HTMLImageElement} img */
  const paintedRect = (img) => {
    const box = img.getBoundingClientRect();
    const cs = getComputedStyle(img);
    const pl = parseFloat(cs.paddingLeft) || 0;
    const pr = parseFloat(cs.paddingRight) || 0;
    const pt = parseFloat(cs.paddingTop) || 0;
    const pb = parseFloat(cs.paddingBottom) || 0;
    const cw = box.width - pl - pr;
    const ch = box.height - pt - pb;
    let w = cw;
    let h = ch;
    if (img.naturalWidth && img.naturalHeight && cs.objectFit === 'contain') {
      const ir = img.naturalWidth / img.naturalHeight;
      if (ir > cw / ch) h = cw / ir;
      else w = ch * ir;
    }
    const [px, py] = (cs.objectPosition || '50% 50%').split(' ').map((v) => (v.endsWith('%') ? parseFloat(v) / 100 : 0.5));
    const left = box.left + pl + (cw - w) * (Number.isFinite(px) ? px : 0.5);
    const top = box.top + pt + (ch - h) * (Number.isFinite(py) ? py : 0.5);
    return { left, top, right: left + w, bottom: top + h, width: w, height: h };
  };

  /** @param {Element} gallery */
  const place = (gallery) => {
    const arrows = gallery.querySelector('slideshow-arrows');
    const container = arrows && arrows.parentElement;
    if (!arrows || !container) return;
    const cRect = container.getBoundingClientRect();
    if (!cRect.width) return;

    // The slide whose centre is nearest the container's centre is the one in view.
    const cx = cRect.left + cRect.width / 2;
    let best = null;
    let bestDist = Infinity;
    for (const slide of container.querySelectorAll('slideshow-slide')) {
      const r = slide.getBoundingClientRect();
      if (!r.width) continue;
      const d = Math.abs(r.left + r.width / 2 - cx);
      if (d < bestDist) { bestDist = d; best = slide; }
    }
    const img = best && best.querySelector('img');
    if (!img) return;
    const p = paintedRect(img);

    const prev = arrows.querySelector('.slideshow-control--previous');
    const next = arrows.querySelector('.slideshow-control--next');
    const iconOffset = (btn, side) => {
      const b = btn && btn.getBoundingClientRect();
      const s = btn && (btn.querySelector('svg') || btn).getBoundingClientRect();
      if (!b || !s) return 0;
      return side === 'start' ? s.left - b.left : b.right - s.right;
    };
    const start = Math.max(0, Math.round(p.left - cRect.left + GAP - iconOffset(prev, 'start')));
    const end = Math.max(0, Math.round(cRect.right - p.right + GAP - iconOffset(next, 'end')));
    arrows.style.setProperty('--nysta-arrows-inset-start', `${start}px`);
    arrows.style.setProperty('--nysta-arrows-inset-end', `${end}px`);
    arrows.dataset.nystaPlaced = 'true';
  };

  const galleries = new Set();
  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      galleries.forEach((g) => (g.isConnected ? place(g) : galleries.delete(g)));
    });
  };

  const bind = () => {
    document.querySelectorAll('.nysta-product .nysta-product-media').forEach((gallery) => {
      if (galleries.has(gallery)) return;
      galleries.add(gallery);
      new ResizeObserver(schedule).observe(gallery);
      gallery.addEventListener('scroll', schedule, { capture: true, passive: true });
      gallery.addEventListener('scrollend', schedule, { capture: true, passive: true });
      gallery.addEventListener('load', schedule, { capture: true });
      gallery.addEventListener('click', () => setTimeout(schedule, 350), { capture: true });
      // Variant changes morph the gallery's contents in place.
      new MutationObserver(schedule).observe(gallery, { childList: true, subtree: true });
    });
    schedule();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
  window.addEventListener('resize', schedule, { passive: true });
  // Section re-renders replace the whole .nysta-product-media; re-bind at most once a frame.
  let bindQueued = false;
  new MutationObserver(() => {
    if (bindQueued) return;
    bindQueued = true;
    requestAnimationFrame(() => { bindQueued = false; bind(); });
  }).observe(document.body, { childList: true, subtree: true });
})();
